import { SectionId } from "@eventr/common";
import type { DraftItem } from "../../utils/merge-draft-items";

// Hotel joins stage the brand-new room as a draft and route straight into its edit
// screen instead of leaving it to be found in a list.
export const STAGE_AND_NAVIGATE_ON_JOIN_SECTIONS = new Set<SectionId>([SectionId.HOTEL]);

/**
 * A booking a quick-join just staged, waiting to be opened by its parent section-item
 * screen once that screen has mounted and can push the booking's own edit screen itself.
 * `draftItem` is the section item the join just staged (the whole item, containing the
 * new booking), seeded into the destination view's draft store.
 */
export interface PendingSubItemState {
  pageId: number;
  /** Absent when the join opens the page's list rather than one booking's own edit screen. */
  subItemId?: string;
  draftItem?: DraftItem;
}

/** Key under react-router navigation `state` carrying a `PendingSubItemState`. */
export const PENDING_SUB_ITEM_STATE_KEY = "pendingSubItem";

// Done or go-back on the destination remounts the consuming screen, so the consumed marker
// is keyed by the history entry for the session.
const consumedHandoffs = new Set<string>();

/** Marks the history entry that carried `pending` as consumed; false when already consumed. */
export function consumePendingHandoff(locationKey: string, pending: PendingSubItemState): boolean {
  const key = `${locationKey}:${pending.pageId}:${pending.subItemId ?? ""}`;
  if (consumedHandoffs.has(key)) return false;
  consumedHandoffs.add(key);
  return true;
}

export function pendingSubItemFromState(state: unknown): PendingSubItemState | undefined {
  if (!state || typeof state !== "object") return undefined;
  const pending = (state as Record<string, unknown>)[PENDING_SUB_ITEM_STATE_KEY];
  if (!pending || typeof pending !== "object") return undefined;
  const { pageId, subItemId, draftItem } = pending as Partial<PendingSubItemState>;
  if (typeof pageId !== "number") return undefined;
  if (subItemId !== undefined && typeof subItemId !== "string") return undefined;
  // A payload with no draftItem, or a malformed one, still parses as pageId/subItemId.
  if (draftItem && typeof draftItem === "object" && typeof (draftItem as DraftItem)._id === "string") {
    return { pageId, subItemId, draftItem: draftItem as DraftItem };
  }
  return { pageId, subItemId };
}
